import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Save, HelpCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Logo from './Logo';
import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const FAQManager = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFaqs();
  }, [token]);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/business/faqs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(res.data.faqs || []);
    } catch {
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const addFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    setFaqs(updated);
  };

  const handleSave = async () => {
    const validFaqs = faqs.filter(f => f.question.trim() && f.answer.trim());
    setSaving(true);
    try {
      await axios.put(`${API}/business/faqs`, validFaqs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(validFaqs);
      toast({ title: 'FAQs saved successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save FAQs', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
        <p className="text-gray-400">Loading FAQs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="faq-manager-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            <h1 className="text-xl font-bold text-white">FAQ Manager</h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-faqs-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-gray-400 text-sm">
            Add frequently asked questions that Mia can answer during calls. The AI will use these to provide accurate responses to callers.
          </p>
        </div>

        <div className="space-y-4" data-testid="faq-list">
          {faqs.length === 0 ? (
            <Card className="bg-gray-900/50 border-white/10">
              <CardContent className="py-12 text-center">
                <HelpCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">No FAQs yet. Add common questions your callers ask.</p>
              </CardContent>
            </Card>
          ) : (
            faqs.map((faq, i) => (
              <Card key={i} className="bg-gray-900/50 border-white/10" data-testid={`faq-item-${i}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Question</label>
                        <Input
                          value={faq.question}
                          onChange={(e) => updateFaq(i, 'question', e.target.value)}
                          placeholder="e.g., What are your business hours?"
                          className="bg-black/30 border-white/10 text-white"
                          data-testid={`faq-question-${i}`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Answer</label>
                        <textarea
                          value={faq.answer}
                          onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                          placeholder="e.g., We're open Monday through Friday, 9 AM to 5 PM."
                          className="w-full bg-black/30 border border-white/10 text-white rounded-md px-3 py-2 text-sm min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-purple-500"
                          data-testid={`faq-answer-${i}`}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => removeFaq(i)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mt-5"
                      data-testid={`faq-delete-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <Button
          onClick={addFaq}
          variant="outline"
          className="w-full mt-4 border-dashed border-white/20 text-gray-400 hover:text-white hover:bg-white/5"
          data-testid="add-faq-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </main>
    </div>
  );
};

export default FAQManager;
